import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, Clock, MapPin, User, Mail, Phone, MessageSquare, CheckCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

function BookingForm({ preselectedService, onBack, userType, city }) {
    const { className: cityName } = useParams();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        service_id: preselectedService?.id || '',
        service_option: '',
        service_option_id: '',
        client_name: '',
        client_email: '',
        client_phone: '',
        preferred_datetime: '',
        alternate_datetime: '',
        address: '',
        location_requested: false,
        request_questionnaire: false,
        notes: '',
        city: city || cityName,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [selectedService, setSelectedService] = useState(preselectedService || null);

    const navigate = useNavigate();

    // Fetch services based on userType
    useEffect(() => {
        fetchServices();
    }, [userType, city]); // Add city as dependency

    const fetchServices = async () => {
        setLoading(true);
        try {
            // Fetch all services first
            const { data: servicesData, error: servicesError } = await supabase
                .from('services')
                .select(`
                *,
                options:service_options(*)
            `)
                .order('id');

            if (servicesError) throw servicesError;

            // Filter services in JavaScript
            const filteredServices = servicesData.filter(service =>
                service.user_types?.includes(userType) || service.user_types?.includes('all')
            );

            // Process the data to add city-specific pricing
            const processedServices = filteredServices.map(service => ({
                ...service,
                options: service.options?.map(option => ({
                    ...option,
                    price: city === "Austin"
                        ? option.price_austin
                        : city === "SanAntonio"
                            ? option.price_san_antonio
                            : option.price_default
                }))
            }));

            setServices(processedServices);

            // If preselected service exists, update it with processed data
            if (preselectedService) {
                const updatedPreselected = processedServices.find(s => s.id === preselectedService.id);
                setSelectedService(updatedPreselected || preselectedService);
            } else {
                // No preselected service, clear selectedService
                setSelectedService(null);
            }
        } catch (error) {
            console.error('Error fetching services:', error);
            setError('Failed to load services');
        } finally {
            setLoading(false);
        }
    };

    const selectedOption = selectedService?.options?.find(opt => opt.name === formData.service_option);

    console.log("Option ID:" , selectedOption?.id);
    formData.service_option_id = selectedOption?.id;

    // Dynamic form configuration based on service ID
    const formConfig = useMemo(() => {
        const serviceId = parseInt(formData.service_id);

        switch (serviceId) {
            case 0: // Consultation
                return {
                    showOptions: true,
                    customOptions: [
                        { name: "Zoom Call", duration: "15min" },
                        { name: "FaceTime Call", duration: "15min" },
                        { name: "Phone Call", duration: "15min" },
                        { name: "Text Conversation", duration: "Unlimited" },
                    ],
                    optionsLabel: "Consultation Type",
                    optionsRequired: true,
                    dateLabel: "Preferred Date & Time",
                    showAlternateDate: false,
                    showPrice: false,
                    showQuestionnaire: false,
                    showLocation: false,
                    addressLabel: "Preferred Location (Optional)",
                    addressRequired: false,
                };

            case 1: // Subscription
                return {
                    showOptions: true,
                    customOptions: [
                        { name: "3 1hr Sessions", duration: "60min" },
                        { name: "3 1.5hr Sessions", duration: "90min" },
                        { name: "3 2hr Sessions", duration: "120min" }
                    ],
                    optionsLabel: "Session Duration",
                    optionsRequired: true,
                    dateLabel: "Tentative Start Date",
                    showAlternateDate: false,
                    showPrice: false,
                    showQuestionnaire: true,
                    showLocation: true,
                    addressLabel: "Service Address",
                    addressRequired: true,
                };

            case 2: // Yoni Massage
            case 3: // Tantric Session
            case 6: // Yoni + Perineal Massage (Pregnancy)
            case 7: // Tantric Pregnancy Session
                return {
                    showOptions: true,
                    optionsLabel: "Select Option",
                    optionsRequired: true,
                    dateLabel: "Preferred Date & Time",
                    showAlternateDate: true,
                    showPrice: true,
                    showQuestionnaire: true,
                    showLocation: true,
                    addressLabel: "Service Address",
                    addressRequired: true,
                };

            case 4: // Custom Session
                return {
                    showOptions: false,
                    dateLabel: "Preferred Date & Time",
                    showAlternateDate: true,
                    showPrice: false,
                    showQuestionnaire: true,
                    showLocation: true,
                    addressLabel: "Service Address",
                    addressRequired: true,
                };

            case 5: // MBT Exclusive Session
                return {
                    showOptions: false,
                    dateLabel: "Preferred Start Date & Time",
                    showAlternateDate: true,
                    showPrice: false,
                    showQuestionnaire: true,
                    showLocation: true,
                    addressLabel: "Service Address",
                    addressRequired: true,
                };

            default:
                return {
                    showOptions: selectedService?.options && selectedService.options.length > 0,
                    optionsLabel: "Select Option",
                    optionsRequired: true,
                    dateLabel: "Preferred Date & Time",
                    showAlternateDate: true,
                    showPrice: true,
                    showQuestionnaire: true,
                    showLocation: true,
                    addressLabel: "Service Address",
                    addressRequired: true,
                };
        }
    }, [formData.service_id, selectedService]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            // Find the selected service option to get its ID
           /*  const selectedOptionObj = selectedService?.options?.find(
                opt => opt.name === formData.service_option
            ); */

            const { data, error: insertError } = await supabase
                .from('appointments_request')
                .insert([{
                    service_id: parseInt(formData.service_id), // Ensure it's a number
                    service_option_id: selectedOption?.id, // Store option ID as number
                    service_option: formData.service_option, // Keep name for backwards compatibility
                    client_name: formData.client_name,
                    client_email: formData.client_email,
                    client_phone: formData.client_phone,
                    preferred_datetime: formData.preferred_datetime,
                    alternate_datetime: formData.alternate_datetime || null,
                    address: formData.address || null,
                    location_requested: formData.location_requested,
                    request_questionnaire: formData.request_questionnaire,
                    city: formData.city,
                    notes: formData.notes || null,
                    status: 'pending',
                    user_id: null, // Will be set if they create an account or log in after booking 
                }])
                .select();

            if (insertError) throw insertError;

            console.log('Appointment created:', data);

            setIsSuccess(true);
            setFormData({
                service_id: '',
                service_option: '',
                service_option_id: '',
                client_name: '',
                client_email: '',
                client_phone: '',
                preferred_datetime: '',
                alternate_datetime: '',
                address: '',
                location_requested: false,
                request_questionnaire: false,
                notes: '',
                city: city || cityName,
                user_id: null,
            });
        } catch (err) {
            console.error('Full error:', err);
            setError(err instanceof Error ? err.message : 'Failed to book appointment');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleServiceChange = (e) => {
        const newServiceId = parseInt(e.target.value); // Ensure it's a number

        // Find the service object from the services array
        const serviceObject = services?.find(s => s.id === newServiceId);

        console.log('Service changed to:', serviceObject); // Debug

        setFormData(prev => ({
            ...prev,
            service_id: newServiceId, // Already a number
            service_option: '' // Reset option when service changes
        }));

        // Update the selected service
        setSelectedService(serviceObject || null);
    };

    // Get the options to display (either custom or from service)
    const displayOptions = formConfig.customOptions || selectedService?.options || [];

    if (loading) {
        return (
            <section className="py-5 px-3 bg-light min-vh-100 d-flex align-items-center">
                <div className="container text-center">
                    <div className="spinner-border text-warning" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading booking form...</p>
                </div>
            </section>
        );
    }

    if (isSuccess) {
        return (
            <section className="py-5 px-3 bg-dark min-vh-100 d-flex align-items-center">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-8 text-center">
                            <CheckCircle className="text-warning mb-4" size={80} />
                            <h2 className="display-4 fw-bold text-warning mb-4">Booking Confirmed!</h2>
                            <p className="lead text-white mb-5">
                                Thank you for choosing Mobile Body Therapy. We've received your appointment request
                                and will contact you shortly to confirm your session details.
                            </p>
                            <div className="d-flex gap-3 justify-content-center">
                                <button
                                    onClick={() => {
                                        setIsSuccess(false);
                                        navigate(-1);
                                    }}
                                    className="btn btn-warning text-dark px-4 py-3 fw-semibold"
                                >
                                    Back to Home
                                </button>
                                <button
                                    onClick={() => setIsSuccess(false)}
                                    className="btn btn-outline-warning px-4 py-3 fw-semibold"
                                >
                                    Book Another
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    console.log('Form Data:', formData);

    return (
        <section className="py-5 px-3 bg-light">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-8">
                        <div className="bg-white rounded shadow-lg p-4 border-top border-warning border-4">
                            <div className="mb-4">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="btn btn-link text-dark text-decoration-none p-0 mb-3"
                                >
                                    ← Back to Services
                                </button>
                                <h2 className="display-6 fw-bold text-dark mb-2">Book Your Appointment</h2>
                                <p className="text-muted">Fill out the form below and we'll confirm your session</p>
                            </div>

                            {error && (
                                <div className="alert alert-danger mb-4" role="alert">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                {/* Service Selection */}
                                <div className="mb-4">
                                    <label className="form-label fw-semibold">Select Service *</label>
                                    <select
                                        name="service_id"
                                        value={formData.service_id}
                                        onChange={handleServiceChange}
                                        required
                                        className="form-select"
                                    >
                                        <option value="" disabled>Choose a service...</option>
                                        {services?.map((service) => (
                                            <option key={service.id} value={service.id}>
                                                {service.name}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedService && selectedService.description && (
                                        <div className="mt-3 p-3 bg-warning bg-opacity-10 rounded border border-warning border-opacity-25">
                                            <p className="text-dark small mb-0">{selectedService.description}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Service Option/Type Selection - Dynamic based on service */}
                                {formConfig.showOptions && displayOptions.length > 0 && (
                                    <div className="mb-4">
                                        <label className="form-label fw-semibold">
                                            {formConfig.optionsLabel} {formConfig.optionsRequired && '*'}
                                        </label>
                                        <select
                                            name="service_option"
                                            value={formData.service_option}
                                            onChange={handleChange}
                                            required={formConfig.optionsRequired}
                                            className="form-select"
                                        >
                                            <option value="" disabled>Choose an option...</option>
                                            {displayOptions.map((option, idx) => (
                                                <option key={idx} value={option.name}>
                                                    {option.name}
                                                    {option.service_id === 1 ?
                                                        option.duration && ` - ${option.duration}` : ""}
                                                    {option.service_id ?
                                                        option.price && option.price !== "Free" && ` - $${option.price}` : ""}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Price Display - Only show when configured and option selected */}
                                {formConfig.showPrice && selectedOption && selectedOption.price && selectedOption.price !== "Free" && (
                                    <div className="alert alert-success mb-4">
                                        <strong>Price:</strong> ${selectedOption.price}
                                        {selectedOption.duration && ` • ${selectedOption.duration}`}
                                    </div>
                                )}

                                {/* Client Information */}
                                <div className="row mb-4">
                                    <div className="col-md-6 mb-3 mb-md-0">
                                        <label className="form-label fw-semibold">
                                            <User className="me-2" size={16} />
                                            Full Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="client_name"
                                            value={formData.client_name}
                                            onChange={handleChange}
                                            required
                                            className="form-control"
                                            placeholder="John Doe"
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">
                                            <Phone className="me-2" size={16} />
                                            Phone Number *
                                        </label>
                                        <input
                                            type="tel"
                                            name="client_phone"
                                            value={formData.client_phone}
                                            onChange={handleChange}
                                            required
                                            className="form-control"
                                            placeholder="(555) 123-4567"
                                        />
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="form-label fw-semibold">
                                        <Mail className="me-2" size={16} />
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        name="client_email"
                                        value={formData.client_email}
                                        onChange={handleChange}
                                        required
                                        className="form-control"
                                        placeholder="john@example.com"
                                    />
                                </div>

                                {/* Date & Time - Dynamic label based on service */}
                                <div className="mb-4">
                                    <label className="form-label fw-semibold">
                                        <Calendar className="me-2" size={16} />
                                        {formConfig.dateLabel} *
                                    </label>
                                    <input
                                        type="datetime-local"
                                        name="preferred_datetime"
                                        value={formData.preferred_datetime}
                                        onChange={handleChange}
                                        required
                                        min={new Date().toISOString().slice(0, 16)}
                                        className="form-control"
                                    />
                                </div>

                                {/* Alternate Date & Time - Only show if configured */}
                                {formConfig.showAlternateDate && (
                                    <div className="mb-4">
                                        <label className="form-label fw-semibold">
                                            <Clock className="me-2" size={16} />
                                            Alternate Date & Time (Optional)
                                        </label>
                                        <input
                                            type="datetime-local"
                                            name="alternate_datetime"
                                            value={formData.alternate_datetime}
                                            onChange={handleChange}
                                            min={new Date().toISOString().slice(0, 16)}
                                            className="form-control"
                                        />
                                        <small className="form-text text-muted">
                                            Provide an alternate time in case your preferred slot isn't available
                                        </small>
                                    </div>
                                )}

                                {/* Location - Only show if configured */}
                                {formConfig.showLocation && (
                                    <div className="mb-4">
                                        <div className="form-check mb-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="location_requested"
                                                id="location_requested"
                                                checked={formData.location_requested}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="location_requested">
                                                I need a location provided
                                            </label>
                                        </div>

                                        {!formData.location_requested && (
                                            <>
                                                <label className="form-label fw-semibold">
                                                    <MapPin className="me-2" size={16} />
                                                    {formConfig.addressLabel} {formConfig.addressRequired && !formData.location_requested && '*'}
                                                </label>
                                                <input
                                                    type="text"
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleChange}
                                                    required={formConfig.addressRequired && !formData.location_requested}
                                                    className="form-control"
                                                    placeholder="123 Main St, Austin, TX 78701"
                                                />
                                                <small className="form-text text-muted">
                                                    We'll come to your home, office, or preferred location in Texas
                                                </small>
                                            </>
                                        )}

                                        {formData.location_requested && (
                                            <div className="alert alert-info">
                                                <small>We'll provide a suitable location for your session and confirm the address with you.</small>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Additional Notes */}
                                <div className="mb-4">
                                    <label className="form-label fw-semibold">
                                        <MessageSquare className="me-2" size={16} />
                                        Additional Notes
                                    </label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        rows={4}
                                        className="form-control"
                                        placeholder="Any special requests, health concerns, or preferences we should know about..."
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-100 btn btn-warning text-dark py-3 fw-semibold"
                                >
                                    {isSubmitting ? 'Booking...' : 'Request Appointment'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default BookingForm;